import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCreateSiteFeedbackMutation } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#b00020',
};

const CATEGORIES = [
  { value: 'bug',   label: 'Bug',   icon: 'bug-fill',           hint: 'Something broken or wrong on a page. ' },
  { value: 'idea',  label: 'Idea',  icon: 'lightbulb-fill',     hint: 'A feature or page you wish existed.' },
  { value: 'other', label: 'Other', icon: 'chat-square-dots',   hint: 'Anything else.' },
];

// Persistent feedback button anchored to the bottom-right corner. Mounted
// once at the layout level so it's available on every page. Captures the
// current pathname + query as page_url so the admin can triage with context.
export default function FeedbackWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [err, setErr] = useState(null);
  const [sentOk, setSentOk] = useState(false);
  const [createFeedback, { isLoading: isSubmitting }] = useCreateSiteFeedbackMutation();

  const close = () => {
    setOpen(false);
    setErr(null);
    setSentOk(false);
    setMessage('');
    setCategory('idea');
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!message.trim()) { setErr('Please tell us what you want us to know.'); return; }
    try {
      await createFeedback({
        category,
        message: message.trim(),
        page_url: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      }).unwrap();
      setSentOk(true);
      setMessage('');
    } catch (e) {
      setErr(e?.data?.detail || 'Could not send feedback.');
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        style={{
          position: 'fixed',
          right: 18, bottom: 18,
          background: brand.goldenrod,
          color: brand.deepBlue,
          border: 0,
          borderRadius: 999,
          padding: '0.55rem 1rem',
          fontWeight: 700, fontSize: '0.85rem',
          boxShadow: '0 6px 20px rgba(10,41,71,0.25)',
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
          zIndex: 1100,
        }}
      >
        <i className="bi bi-megaphone-fill" />
        Hub Feedback
      </button>

      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(10,41,71,0.55)',
            zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 520, boxShadow: '0 12px 40px rgba(10,41,71,0.3)', overflow: 'hidden' }}
          >
            <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.9rem 1.1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <i className="bi bi-megaphone-fill" style={{ color: brand.goldenrod, fontSize: '1.25rem' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: brand.goldenrod, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Help us improve
                </div>
                <h5 style={{ color: 'white', fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>Send feedback</h5>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.1rem', cursor: 'pointer' }}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>

            {sentOk ? (
              <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                <i className="bi bi-check-circle-fill" style={{ fontSize: '2.5rem', color: brand.green, display: 'block', marginBottom: '0.5rem' }} />
                <h5 style={{ color: brand.green, fontWeight: 800, margin: 0 }}>Thanks!</h5>
                <p style={{ color: brand.charcoal, margin: '0.4rem 0 1rem' }}>
                  Your feedback was sent. Anything else?
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={() => { setSentOk(false); }}
                    style={{ background: 'transparent', border: `1px solid ${brand.softGray}`, color: brand.deepBlue, borderRadius: 8, padding: '0.4rem 0.9rem', fontWeight: 600, cursor: 'pointer' }}
                  >
                    Send another
                  </button>
                  <button
                    type="button"
                    onClick={close}
                    style={{ background: brand.tealBlue, border: 0, color: 'white', borderRadius: 8, padding: '0.4rem 0.9rem', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} style={{ padding: '1rem 1.1rem 1.2rem' }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 6 }}>
                    Category
                  </label>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    {CATEGORIES.map(c => {
                      const active = category === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => setCategory(c.value)}
                          style={{
                            flex: '1 1 0',
                            minWidth: 90,
                            background: active ? brand.deepBlue : 'white',
                            color: active ? 'white' : brand.deepBlue,
                            border: `1px solid ${active ? brand.deepBlue : brand.softGray}`,
                            borderRadius: 8,
                            padding: '0.5rem 0.6rem',
                            fontWeight: 700, fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                          }}
                        >
                          <i className={`bi bi-${c.icon}`} />
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                  <p style={{ color: brand.charcoal, fontSize: '0.75rem', margin: '0.35rem 0 0' }}>
                    {CATEGORIES.find(c => c.value === category)?.hint}
                  </p>
                </div>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: brand.charcoal, marginBottom: 6 }}>
                    What&apos;s on your mind?
                  </label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={5}
                    maxLength={4000}
                    placeholder="Tell us what you noticed, what you'd like to see, or anything else."
                    style={{ width: '100%', border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.55rem 0.7rem', fontSize: '0.9rem', resize: 'vertical', color: brand.deepBlue, outline: 'none' }}
                  />
                  <div style={{ color: brand.charcoal, fontSize: '0.7rem', textAlign: 'right', marginTop: 2 }}>
                    {message.length} / 4000
                  </div>
                </div>

                {err && (
                  <div style={{ background: 'rgba(176,0,32,0.08)', color: brand.red, border: `1px solid rgba(176,0,32,0.25)`, borderRadius: 8, padding: '0.45rem 0.7rem', fontSize: '0.825rem', marginBottom: '0.75rem' }}>
                    {err}
                  </div>
                )}

                <p style={{ color: brand.charcoal, fontSize: '0.72rem', margin: '0 0 0.75rem' }}>
                  Thank you for your time - we value your opinion!
                </p>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={close} disabled={isSubmitting} style={{ background: 'transparent', color: brand.charcoal, border: `1px solid ${brand.softGray}`, borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} style={{ background: brand.tealBlue, color: 'white', border: 0, borderRadius: 8, padding: '0.5rem 1.1rem', fontWeight: 700, cursor: 'pointer' }}>
                    {isSubmitting ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
